import React, { useEffect } from 'react'

import {
  Button,
  Fieldset,
  InputUI,
  ModalBox,
  RadioButton,
  useModalContext,
} from '@fourthwall/components'
import { useFormik } from '@fourthwall/utils/dist/formik'

import type { PaymentMethodItem } from '../../PaymentMethods.types'
import { PaymentMethodsList } from '../PaymentMethodsList/PaymentMethodsList'
import type { FormValues } from './SelectNewDefaultPaymentMethod.types'
import { validationSchema } from './consts'

export type SelectNewDefaultPaymentMethodProps = {
  removedPaymentMethod: PaymentMethodItem
  remainingPaymentMethods: PaymentMethodItem[]
  onSubmit: (values: Pick<FormValues, 'defaultPaymentMethodId'>) => void
}

export const SelectNewDefaultPaymentMethod = ({
  removedPaymentMethod,
  remainingPaymentMethods,
  onSubmit,
}: SelectNewDefaultPaymentMethodProps) => {
  const { close } = useModalContext()
  const formik = useFormik<FormValues>({
    initialValues: {
      choice: 'select-new-default',
      removedPaymentMethodId: removedPaymentMethod.id,
      defaultPaymentMethodId: '',
    },
    validationSchema,
    onSubmit: ({ defaultPaymentMethodId }) => {
      onSubmit({ defaultPaymentMethodId })
      close()
    },
  })

  useEffect(() => {
    if (formik.values.choice === 'dont-select-new-default') {
      formik.setFieldValue('defaultPaymentMethodId', '')
      formik.setFieldTouched('defaultPaymentMethodId', false)
    }
  }, [formik.values.choice])

  const paymentMethods = remainingPaymentMethods.map((paymentMethod) => {
    const selected = formik.values.defaultPaymentMethodId === paymentMethod.id

    return {
      ...paymentMethod,
      selected,
      onClick: () => {
        formik.setFieldValue('defaultPaymentMethodId', selected ? '' : paymentMethod.id)
        if (!selected && formik.values.choice !== 'select-new-default') {
          formik.setFieldValue('choice', 'select-new-default')
        }
      },
    }
  })

  const renderDescription = () => {
    if (removedPaymentMethod.type === 'CARD') {
      return (
        <p>
          You’re about to remove a default payment method ({removedPaymentMethod.type} ending in{' '}
          {removedPaymentMethod.number}). We recommend selecting a new default payment method.
        </p>
      )
    }

    // PAYPAL
    return (
      <p>
        You’re about to remove a default payment method (PayPal: {removedPaymentMethod.email}). We
        recommend selecting a new default payment method.
      </p>
    )
  }

  return (
    <form onSubmit={formik.handleSubmit}>
      <ModalBox
        title="Select a new default payment method"
        buttons={[
          <Button label="Cancel" onClick={close} />,
          <Button label="Proceed" appearance="primary" type="submit" />,
        ]}
        onClose={close}
      >
        {renderDescription()}
        <div style={{ marginTop: 20 }}>
          <Fieldset>
            <Fieldset.Row>
              <Fieldset.Item>
                <RadioButton
                  label="Select a new default payment method:"
                  checkedValue="select-new-default"
                  {...formik.getFieldProps('choice')}
                />
                <div style={{ marginTop: 12, marginLeft: 36 }}>
                  <PaymentMethodsList paymentMethods={paymentMethods} selectable />
                  {formik.errors.defaultPaymentMethodId && (
                    <InputUI.Error>{formik.errors.defaultPaymentMethodId}</InputUI.Error>
                  )}
                </div>
              </Fieldset.Item>
            </Fieldset.Row>
            <Fieldset.Row>
              <Fieldset.Item>
                <RadioButton
                  label="I don’t want to use a default payment method"
                  suggestion="Not having a default payment method will result in transactions that incur losses (such as giveaways) being blocked if your account balance is not sufficient to cover the cost."
                  checkedValue="dont-select-new-default"
                  {...formik.getFieldProps('choice')}
                />
              </Fieldset.Item>
            </Fieldset.Row>
          </Fieldset>
        </div>
      </ModalBox>
    </form>
  )
}
